/**
 * 输入输出全屏展示组件（带下载进度条）
 * zxw
 * 创建时间 2026-02-02
 */

import CNF from '../../incs/Config';
import {
    locale,
    Html2PDF,
} from '../../incs/FunHelper';
import { MarkdownToDocx } from '../../incs/MarkdownToWord.js';

import { useState, useEffect, useRef, useCallback } from 'react';
import {
    ConfigProvider,
    Modal,
    Typography,
    Button,
    Row,
    Col,
    Select,
    message,
    Input,
    Progress,
    Flex,
} from 'antd';
import { XMarkdown } from '@ant-design/x-markdown';
import Latex from '@ant-design/x-markdown/plugins/Latex';   // 公式
import { CodeHighlighter, } from '@ant-design/x';
import '@ant-design/x-markdown/themes/light.css';
import html2canvas from 'html2canvas';

// 思维链
import XinYuanThink from './XinYuanThink';
import {
    ImageSkeleton,
    IncompleteLink,
    TableSkeleton,
    HtmlSkeleton,
    IncompleteEmphasis,
    IncompleteInlineCode,
    WelcomeCard,
    Code,
} from '../../components/Incomplete';

// 图表
import ChartPiePercent from './reportTemplates/ChartPiePercent';
import ChartLineUTACD from './reportTemplates/ChartLineUTACD';
import ChartLineUTECY from './reportTemplates/ChartLineUTECY';
import ChartLineDateCount from './reportTemplates/ChartLineDateCount';
import ChartIntervalTypeCount from './reportTemplates/ChartIntervalTypeCount';
import ChartILYearAmount from './reportTemplates/ChartILYearAmount';
import ChartIntervalMonthIOAmount from './reportTemplates/ChartIntervalMonthIOAmount';

import XZTLOGO from '../../images/logo-head.png';
import PAGEHEADERRIGHT from '../../images/page-header-right.png';

const { Text } = Typography;

const IOPutFullScreen = (props) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [messageApi, contextHolder] = message.useMessage();

    const [exportFormat, setExportFormat] = useState('PDF');
    const [fileName, setFileName] = useState(props.appInfo?.app_name);

    const progressTimerRef = useRef(null); // 进度模拟定时器

    // 清理定时器
    const clearProgressTimer = useCallback(() => {
        if (progressTimerRef.current) {
            clearInterval(progressTimerRef.current);
            progressTimerRef.current = null;
        }
    }, []);

    // 模拟进度：从当前值逐步增加到 90，步进为 10，间隔 500ms
    const startSimulateProgress = useCallback(() => {
        setDownloadProgress(0);
        progressTimerRef.current = setInterval(() => {
            setDownloadProgress(prev => {
                if (prev >= 90) {
                    clearProgressTimer();
                    return 100;
                }
                return prev + 10;
            });
        }, 200);
    }, [clearProgressTimer]);

    // 取消操作：关闭弹窗并重置状态
    const cancel = useCallback(() => {
        setOpen(false);
        setLoading(false);
        setExportFormat('PDF');
        setDownloadProgress(0);
        clearProgressTimer();
    }, [clearProgressTimer]);

    // 组件卸载时清除定时器
    useEffect(() => {
        return () => {
            clearProgressTimer();
        };
    }, [clearProgressTimer]);

    useEffect(() => {
        setOpen(props.open);
    }, [props]);

    const className = props.isDarkMode ? 'x-markdown-dark' : 'x-markdown-light';

    // 截图所有图表（与原来一致）
    const captureChartImages = async () => {
        const printContainer = document.getElementById('print-info-id');
        if (!printContainer) {
            console.warn('未找到全屏容器，可能状态异常');
            return [];
        }
        const chartElements = printContainer.querySelectorAll('[data-chart-type="true"]');
        await new Promise(resolve => setTimeout(resolve, CNF.animateDuration + 200));
        const base64Array = [];
        for (const element of chartElements) {
            try {
                const canvas = await html2canvas(element, {
                    backgroundColor: '#ffffff',
                    scale: 2,
                    useCORS: true,
                });
                base64Array.push(canvas.toDataURL('image/png'));
            } catch (err) {
                console.error('图表截图失败:', err);
                base64Array.push(null);
            }
        }
        return base64Array;
    };

    const replaceChartsWithImages = (markdown, base64Array) => {
        const chartRegex = /<chart-[\w-]+[\s\S]*?<\/chart-[\w-]+>/g;
        let index = 0;
        return markdown.replace(chartRegex, (match) => {
            const base64 = base64Array[index];
            index++;
            if (!base64) return match;
            return `![图表](${base64})`;
        });
    };

    const toPdf = async () => {
        await Html2PDF('print-info-id', fileName);
    };

    const toWord = async (markdownText) => {
        const chartImages = await captureChartImages();
        const processed = replaceChartsWithImages(markdownText, chartImages);
        await MarkdownToDocx(processed, { fileName });
    };

    const download = async () => {
        if (!fileName) {
            messageApi.error('文件名称不能为空', CNF.messageDuration);
            return;
        }

        setLoading(true);
        startSimulateProgress(); // 启动模拟进度条

        try {
            if (exportFormat === 'WORD') {
                await toWord(props.content);
            } else {
                await toPdf();
            }
            // 导出成功，进度走到 100%
            setDownloadProgress(100);
            setTimeout(() => {
                messageApi.success('下载成功！', CNF.messageDuration);
                cancel(); // 关闭弹窗并重置进度
            }, CNF.messageDuration * 1000);
        } catch (error) {
            messageApi.error('导出失败，请重试', CNF.messageDuration);
        } finally {
            setTimeout(() => {
                clearProgressTimer(); // 停止模拟进度
                setLoading(false);
            }, CNF.messageDuration * 1000);
        }
    };

    // 根据 loading 状态动态构建底部操作区
    const footer = props.title === '最终输出' ? (
        loading ? (
            <Flex justify='flex-end' align='center' gap={10} style={{ padding: '0 24px' }}>
                <Progress
                    size={32}
                    type="circle"
                    percent={downloadProgress}
                    status="active"
                    strokeColor={{
                        '0%': props.colorPrimary,
                        '100%': props.colorPrimary,
                    }}
                />
                <Button type='primary' loading>下载中</Button>
            </Flex>
        ) : (
            <Flex justify='flex-end' align='center' gap={10} style={{ padding: '0 24px' }}>
                <Input
                    tabIndex={-1}
                    style={{ width: '180px', }}
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                />
                <Select
                    tabIndex={-1}
                    popupMatchSelectWidth={false}
                    suffixIcon={CNF.exportFormats.find(item => item.value === exportFormat)?.icon}
                    options={CNF.exportFormats.map((item) => ({
                        label: item.name,
                        value: item.value,
                    }))}
                    value={exportFormat}
                    onChange={(value) => setExportFormat(value)}
                />
                <Button onClick={cancel}>取消</Button>
                <Button type='primary' onClick={download}>下载</Button>
            </Flex>
        )
    ) : null;

    return (
        <ConfigProvider
            locale={locale}
            theme={{
                token: { colorPrimary: global.THEMECOLOR },
            }}
        >
            {contextHolder}
            <Modal
                title={props.title === '最终输出' ? null : <span style={{ color: CNF.baseTextColor }}>{props.title}</span>}
                open={open}
                onCancel={cancel}
                width={820}
                closeIcon={props.title === '最终输出' ? false : true}
                style={{ top: 0 }}
                styles={{
                    container: { paddingLeft: 0, paddingRight: 0 },
                    header: {
                        paddingLeft: 20,
                        paddingBottom: 12,
                        borderBottom: `1px solid ${CNF.baseBorderSolidColor}`,
                    },
                    body: { padding: 20 },
                }}
                destroyOnHidden={true}
                footer={footer}
            >
                <div
                    id='print-info-id'
                    style={{
                        maxHeight: 'calc(100vh - 160px)',
                        scrollbarColor: '#d9d9d9 transparent',
                        scrollbarWidth: 'thin',
                        overflowY: 'auto',
                    }}
                >
                    {props.title === '最终输出' ? (
                        <Row style={{ margin: '10px 15px' }}>
                            <Col span={12}><img src={XZTLOGO} height='45px' alt='' /></Col>
                            <Col span={12} align='right'><img src={PAGEHEADERRIGHT} height='35px' alt='' /></Col>
                        </Row>
                    ) : ''}
                    {props.format === 'Json' ? (
                        <CodeHighlighter
                            lang='json'
                            styles={{
                                header: { display: 'none' },
                                code: { border: 0, background: '#fff' }
                            }}
                        >
                            {JSON.stringify(props.content, null, 2)}
                        </CodeHighlighter>
                    ) : props.format === 'Text' ? (
                        <Text style={{ color: CNF.baseTextColor, fontSize: CNF.fontSizeSM }}>
                            {JSON.stringify(props.content)}
                        </Text>
                    ) : (
                        <XMarkdown
                            content={props.content}
                            className={className}
                            paragraphTag='div'
                            openLinksInNewTab
                            dompurifyConfig={{ ADD_ATTR: ['icon', 'description'] }}
                            style={{
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                overflowX: 'hidden',
                                padding: 8,
                                width: 'auto',
                            }}
                            components={{
                                code: Code,
                                welcome: WelcomeCard,
                                'think': XinYuanThink,
                                'incomplete-image': ImageSkeleton,
                                'incomplete-link': IncompleteLink,
                                'incomplete-table': TableSkeleton,
                                'incomplete-html': HtmlSkeleton,
                                'incomplete-emphasis': IncompleteEmphasis,
                                'incomplete-inline-code': IncompleteInlineCode,
                                'chart-pie-percent': ChartPiePercent,
                                'chart-line-utacd': ChartLineUTACD,
                                'chart-line-utecy': ChartLineUTECY,
                                'chart-line-date-count': ChartLineDateCount,
                                'chart-interval-type-count': ChartIntervalTypeCount,
                                'chart-il-year-amount': ChartILYearAmount,
                                'chart-interval-month-io-amount': ChartIntervalMonthIOAmount,
                            }}
                            config={{
                                extensions: Latex(),
                            }}
                        />
                    )}
                </div>
            </Modal>
        </ConfigProvider>
    );
};

export default IOPutFullScreen;